import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Brain, Check, RefreshCw, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Diff por líneas (LCS) — suficiente para revisar prompts
const lineDiff = (a, b) => {
  const A = (a || '').split('\n'), B = (b || '').split('\n');
  const m = A.length, n = B.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < m && j < n) {
    if (A[i] === B[j]) { out.push({ t: ' ', s: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: '-', s: A[i] }); i++; }
    else { out.push({ t: '+', s: B[j] }); j++; }
  }
  while (i < m) out.push({ t: '-', s: A[i++] });
  while (j < n) out.push({ t: '+', s: B[j++] });
  return out;
};

const DiffView = ({ current, proposed, testId }) => {
  const rows = useMemo(() => lineDiff(current, proposed), [current, proposed]);
  const added = rows.filter((r) => r.t === '+').length, removed = rows.filter((r) => r.t === '-').length;
  return (
    <div data-testid={testId}>
      <p className="mb-2 text-[11px] text-text-muted"><span className="text-emerald-300">+{added}</span> · <span className="text-rose-300">−{removed}</span> líneas</p>
      <pre className="max-h-80 overflow-auto rounded-xl border border-border-subtle bg-background/70 p-3 text-[11.5px] leading-relaxed">
        {rows.map((r, k) => (
          <div key={k} className={r.t === '+' ? 'bg-emerald-500/10 text-emerald-200' : r.t === '-' ? 'bg-rose-500/10 text-rose-300 line-through decoration-rose-400/40' : 'text-text-muted'}>
            <span className="mr-2 select-none opacity-60">{r.t}</span>{r.s || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
};

const PendingCard = ({ d, onDecide, busy }) => (
  <div className="glass-card space-y-4 p-5" data-testid={`prompt-pending-${d.version_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{d.client_id} · {d.install_name}</p>
        <h4 className="mt-1 text-base font-semibold text-text-heading">Versión {d.version} <span className="ml-2 rounded-full border border-border-subtle px-2 py-0.5 text-[10px] text-text-muted">{d.source}</span></h4>
        <p className="mt-1 text-xs text-text-muted">{d.change_note || 'Sin nota'} · {new Date(d.created_at).toLocaleString('es-PE')}</p>
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={!!busy} onClick={() => onDecide(d, 'reject')} data-testid={`prompt-reject-${d.version_id}`}
          className="inline-flex items-center gap-1 rounded-full border border-rose-400/30 px-4 py-2 text-xs font-semibold text-rose-200 transition hover:bg-rose-500/10 disabled:opacity-50"><X className="h-3.5 w-3.5" /> Rechazar</button>
        <button type="button" disabled={!!busy} onClick={() => onDecide(d, 'approve')} data-testid={`prompt-approve-${d.version_id}`}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-background transition hover:opacity-90 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Aprobar y aplicar</button>
      </div>
    </div>
    <DiffView current={d.current_prompt} proposed={d.system_prompt} testId={`prompt-diff-${d.version_id}`} />
  </div>
);

export const PromptApprovalPanel = ({ authFetch }) => {
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (notify = false) => {
    try {
      setLoading(true);
      const [p, s] = await Promise.all([authFetch(`${API}/learning/prompts/pending`), authFetch(`${API}/learning/status`)]);
      const pj = await p.json(), sj = await s.json();
      if (!p.ok) throw new Error(pj.detail || 'No se pudo cargar');
      setPending(pj.pending || []); setStats(sj);
      if (notify) toast.success('Aprendizaje actualizado');
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const decide = async (d, action) => {
    try {
      setBusy(d.version_id);
      const body = action === 'approve' ? { approved_by: 'admin', note: 'Aprobado desde Panel Aprendizaje' } : { rejected_by: 'admin', reason: 'Rechazado desde Panel Aprendizaje' };
      const r = await authFetch(`${API}/learning/prompts/versions/${d.version_id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error');
      toast.success(action === 'approve' ? (j.applied_to_installation ? `v${d.version} aplicada a ${d.install_name}` : `v${d.version} aprobada (simulado, flag OFF)`) : `v${d.version} rechazada`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const st = stats?.stats || {};
  return (
    <section className="space-y-6" data-testid="prompt-approval-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Fase 7 · Aprendizaje supervisado</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Prompts pendientes de aprobación</h3>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="prompt-approval-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          { icon: Brain, label: 'Pendientes', value: pending.length, helper: 'borradores por revisar', id: 'learning-stat-pending' },
          { icon: ThumbsUp, label: 'Feedback total', value: st.feedback_total ?? 0, helper: 'señales recibidas', id: 'learning-stat-feedback' },
          { icon: ThumbsDown, label: 'Negativas', value: st.feedback_down ?? 0, helper: '👎 de clientes', id: 'learning-stat-down' },
          { icon: Check, label: 'Base conocimiento', value: st.kb_entries ?? 0, helper: 'entradas activas', id: 'learning-stat-kb' },
        ].map((c) => (
          <div key={c.id} className="glass-card p-5" data-testid={c.id}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{c.label}</p>
                <p className="mt-3 text-3xl font-bold text-text-heading">{c.value}</p>
                <p className="mt-2 text-xs text-text-muted">{c.helper}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><c.icon className="h-5 w-5" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4" data-testid="prompt-pending-list">
        {pending.map((d) => <PendingCard key={d.version_id} d={d} onDecide={decide} busy={busy} />)}
        {!loading && !pending.length && (
          <p className="glass-card p-6 text-sm text-text-muted" data-testid="prompt-pending-empty">
            No hay prompts pendientes. Las sugerencias llegan solas cuando Aynara recibe 👎 (o lánzalas con <code>POST /api/learning/prompts/suggest</code>).
          </p>
        )}
      </div>
    </section>
  );
};

export default PromptApprovalPanel;
